import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Search, Package, Upload, Sparkles, Wand2, BrainCircuit, Globe } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Catalogo materiali impianti elettrici — `/admin/materiali-catalogo`.
 *
 * Sezioni: TVCC, allarmi, domotica, frutti elettrici, automazione,
 * illuminazione, cavi, quadri, reti LAN, rivelazione incendio, ecc.
 *
 * Features: lista, ricerca, filtro per categoria/produttore, create/edit/delete,
 * import Excel massivo, seed prodotti standard, AI search & import.
 */
export default function MaterialiCatalogoPage() {
  const [items, setItems] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [produttoriList, setProduttoriList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterProduttore, setFilterProduttore] = useState('all');
  // iter100n: debounced search — evita refetch a ogni tasto
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchPending, setSearchPending] = useState(false);
  useEffect(() => {
    setSearchPending(true);
    const t = setTimeout(() => { setDebouncedSearch(search); setSearchPending(false); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importingMetel, setImportingMetel] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [totalCatalog, setTotalCatalog] = useState(0);
  // iter100m: dialog "Aggiungi al Magazzino"
  const [addToMagDialog, setAddToMagDialog] = useState(false);
  const [addToMagRows, setAddToMagRows] = useState([]);  // [{catalogo_id, codice, descrizione, qty_initial, qty_min, location}]
  const [addToMagBusy, setAddToMagBusy] = useState(false);
  const emptyForm = { codice: '', descrizione: '', categoria: 'tvcc', sottocategoria: '', produttore: '', prezzo_listino: '', prezzo_acquisto: '', prezzo_vendita: '', sconto_pct: '', unita_misura: 'pz', ean: '', note: '' };
  const [form, setForm] = useState(emptyForm);
  // AI state
  const [aiSearchActive, setAiSearchActive] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [aiSearchBusy, setAiSearchBusy] = useState(false);
  const [aiImportDialog, setAiImportDialog] = useState(false);
  const [aiImportForm, setAiImportForm] = useState({ prompt: '', categoria: '', max_items: 20 });
  const [aiImportBusy, setAiImportBusy] = useState(false);
  const [aiLookupDialog, setAiLookupDialog] = useState(false);
  const [aiLookupForm, setAiLookupForm] = useState({ codice: '', produttore: '', url: '' });
  const [aiLookupBusy, setAiLookupBusy] = useState(false);
  const [aiLookupResult, setAiLookupResult] = useState(null);
  const [aiDescribeBusy, setAiDescribeBusy] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategoria && filterCategoria !== 'all') params.append('categoria', filterCategoria);
      if (filterProduttore && filterProduttore !== 'all') params.append('produttore', filterProduttore);
      if (debouncedSearch) params.append('search', debouncedSearch);
      const [list, cats, prods, cnt] = await Promise.all([
        axios.get(`${API}/materiali-catalogo/list?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/materiali-catalogo/categorie`, { withCredentials: true }),
        axios.get(`${API}/materiali-catalogo/produttori`, { withCredentials: true }),
        axios.get(`${API}/materiali-catalogo/count?${params.toString()}`, { withCredentials: true }).catch(() => ({ data: { total: 0 } })),
      ]);
      setItems(list.data || []);
      setCategorie(cats.data || []);
      setProduttoriList(prods.data || []);
      setTotalCatalog(cnt.data?.total || 0);
    } catch {
      toast.error('Errore caricamento catalogo');
    } finally {
      setLoading(false);
    }
  }, [filterCategoria, filterProduttore, debouncedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      prezzo_listino: parseFloat(form.prezzo_listino) || 0,
      prezzo_acquisto: parseFloat(form.prezzo_acquisto) || 0,
      prezzo_vendita: parseFloat(form.prezzo_vendita) || 0,
    };
    // sconto_pct: se l'utente NON l'ha scritto, lasciarlo undefined così il
    // backend lo calcola automaticamente da listino/acquisto.
    if (form.sconto_pct !== '' && form.sconto_pct !== null && form.sconto_pct !== undefined) {
      payload.sconto_pct = parseFloat(form.sconto_pct) || 0;
    } else {
      delete payload.sconto_pct;
    }
    try {
      if (editing) {
        await axios.put(`${API}/materiali-catalogo/${editing.id}`, payload, { withCredentials: true });
        toast.success('Materiale aggiornato');
      } else {
        await axios.post(`${API}/materiali-catalogo`, payload, { withCredentials: true });
        toast.success('Materiale creato');
      }
      setOpenCreate(false);
      setEditing(null);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      codice: item.codice || '', descrizione: item.descrizione || '',
      categoria: item.categoria || 'tvcc', sottocategoria: item.sottocategoria || '',
      produttore: item.produttore || '',
      prezzo_listino: item.prezzo_listino ? String(item.prezzo_listino) : '',
      prezzo_acquisto: item.prezzo_acquisto ? String(item.prezzo_acquisto) : '',
      prezzo_vendita: item.prezzo_vendita ? String(item.prezzo_vendita) : '',
      sconto_pct: item.sconto_pct ? String(item.sconto_pct) : '',
      unita_misura: item.unita_misura || 'pz',
      ean: item.ean || '', note: item.note || '',
    });
    setOpenCreate(true);
  };

  // iter100j: selezione multipla
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSelectAll = (checked) => setSelectedIds(checked ? new Set(items.map(i => i.id)) : new Set());
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.size} materiali selezionati?`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/materiali-catalogo/bulk-delete`, { ids: Array.from(selectedIds) }, { withCredentials: true });
      toast.success(`${r.data.deleted} materiali eliminati`);
      setSelectedIds(new Set());
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    } finally { setBulkBusy(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo materiale?')) return;
    try {
      await axios.delete(`${API}/materiali-catalogo/${id}`, { withCredentials: true });
      toast.success('Eliminato');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  // iter100m: apre dialog "Aggiungi al Magazzino" (bulk o singolo)
  const openAddToMagFromSelection = () => {
    const rows = items.filter(i => selectedIds.has(i.id)).map(i => ({
      catalogo_id: i.id, codice: i.codice, descrizione: i.descrizione, produttore: i.produttore,
      prezzo_listino: i.prezzo_listino, unita_misura: i.unita_misura || 'pz',
      qty_initial: 0, qty_min: 0, location: '',
    }));
    if (rows.length === 0) { toast.info('Nessun articolo selezionato'); return; }
    setAddToMagRows(rows);
    setAddToMagDialog(true);
  };
  const openAddSingleToMag = (item) => {
    setAddToMagRows([{
      catalogo_id: item.id, codice: item.codice, descrizione: item.descrizione, produttore: item.produttore,
      prezzo_listino: item.prezzo_listino, unita_misura: item.unita_misura || 'pz',
      qty_initial: 0, qty_min: 0, location: '',
    }]);
    setAddToMagDialog(true);
  };
  const submitAddToMag = async () => {
    if (addToMagRows.length === 0) return;
    setAddToMagBusy(true);
    try {
      const r = await axios.post(`${API}/magazzino/materiali/import-catalogo-selected`, {
        items: addToMagRows.map(row => ({
          catalogo_id: row.catalogo_id,
          qty_initial: parseFloat(row.qty_initial) || 0,
          qty_min: parseFloat(row.qty_min) || 0,
          location: row.location || '',
        })),
      }, { withCredentials: true, timeout: 120000 });
      const { created, updated, skipped } = r.data;
      toast.success(`Magazzino: ${created} nuovi, ${updated} aggiornati${skipped ? `, ${skipped} saltati` : ''}`);
      setAddToMagDialog(false);
      setAddToMagRows([]);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiunta al Magazzino');
    } finally {
      setAddToMagBusy(false);
    }
  };


  const handleDeleteAll = async () => {
    const total = totalCatalog || items.length;
    if (total === 0) { toast.info('Nessun materiale da eliminare'); return; }
    const confirmText = window.prompt(`Stai per eliminare TUTTI i ${total.toLocaleString('it-IT')} materiali del catalogo.\n\nDigita ELIMINA per confermare:`);
    if (confirmText !== 'ELIMINA') { toast.info('Operazione annullata'); return; }
    const t = toast.loading('Eliminazione in corso…');
    try {
      const r = await axios.post(`${API}/materiali-catalogo/delete-all`, null, { params: { confirm: 'YES' }, withCredentials: true, timeout: 120000 });
      toast.dismiss(t);
      toast.success(`${r.data.deleted} materiali eliminati`);
      setSelectedIds(new Set());
      fetchData();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || 'Errore eliminazione totale');
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('Caricare il catalogo standard di prodotti del settore (~65 articoli)?\nNon sovrascrive i prodotti esistenti.')) return;
    try {
      const r = await axios.post(`${API}/materiali-catalogo/seed-defaults`, {}, { withCredentials: true });
      toast.success(`Catalogo seedato: ${r.data.inserted} nuovi, ${r.data.updated} aggiornati`);
      fetchData();
    } catch { toast.error('Errore seed'); }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setImporting(true);
    try {
      const r = await axios.post(`${API}/materiali-catalogo/import-excel`, fd, { withCredentials: true });
      toast.success(`Import OK: ${r.data.imported} nuovi, ${r.data.updated} aggiornati${r.data.errors?.length ? ` (${r.data.errors.length} errori)` : ''}`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import fallito');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleImportMetel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    // Warning per file molto grandi (METEL può avere 50k+ righe)
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    if (file.size > 3 * 1024 * 1024) {
      const ok = window.confirm(`File METEL ~${sizeMB}MB — l'import può richiedere fino a 2 minuti.\n\nProcedere?`);
      if (!ok) { e.target.value = ''; return; }
    }
    setImportingMetel(true);
    const t = toast.loading(`Import METEL in corso (${sizeMB}MB)…`);
    try {
      // Timeout 5 min per file grandi
      const r = await axios.post(`${API}/materiali-catalogo/import-metel`, fd, { withCredentials: true, timeout: 300000 });
      const fmt = r.data.format === 'fixed-width-metel' ? 'fixed-width METEL' : `delimitato (${r.data.delimiter})`;
      toast.dismiss(t);
      toast.success(`METEL ${fmt}: ${r.data.inserted} nuovi, ${r.data.updated} aggiornati, ${r.data.skipped} saltati${r.data.errors?.length ? ` (${r.data.errors.length} errori)` : ''}`);
      fetchData();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || err?.message || 'Import METEL fallito');
    } finally {
      setImportingMetel(false);
      e.target.value = '';
    }
  };

  // -------------------------- AI handlers --------------------------
  const handleAiSearch = async () => {
    if (!search || search.trim().length < 2) {
      toast.error('Scrivi almeno 2 caratteri'); return;
    }
    setAiSearchBusy(true);
    setAiSearchActive(true);
    try {
      const r = await axios.post(`${API}/materiali-catalogo/ai-search`,
        { query: search.trim(), limit: 20 }, { withCredentials: true });
      const matches = r.data?.matches || [];
      setAiResults(matches);
      if (matches.length === 0) toast.warning('Nessun risultato AI per questa query');
      else toast.success(`AI ha trovato ${matches.length} risultati`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ricerca AI fallita');
      setAiSearchActive(false);
    } finally {
      setAiSearchBusy(false);
    }
  };

  const clearAiSearch = () => {
    setAiSearchActive(false);
    setAiResults([]);
  };

  const handleAiImport = async (e) => {
    e.preventDefault();
    if (!aiImportForm.prompt || aiImportForm.prompt.trim().length < 10) {
      toast.error('Descrivi la lista (almeno 10 caratteri)'); return;
    }
    setAiImportBusy(true);
    try {
      const r = await axios.post(`${API}/materiali-catalogo/ai-import-prompt`, {
        prompt: aiImportForm.prompt.trim(),
        categoria: aiImportForm.categoria || null,
        max_items: Number(aiImportForm.max_items) || 20,
      }, { withCredentials: true });
      toast.success(`Import AI: ${r.data.inserted} nuovi, ${r.data.updated} aggiornati`);
      setAiImportDialog(false);
      setAiImportForm({ prompt: '', categoria: '', max_items: 20 });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import AI fallito');
    } finally {
      setAiImportBusy(false);
    }
  };

  const handleAiLookup = async (e) => {
    e.preventDefault();
    if (!aiLookupForm.codice && !aiLookupForm.produttore && !aiLookupForm.url) {
      toast.error('Inserisci almeno codice, produttore o URL'); return;
    }
    setAiLookupBusy(true);
    setAiLookupResult(null);
    try {
      const r = await axios.post(`${API}/materiali-catalogo/ai-lookup`, aiLookupForm, { withCredentials: true });
      setAiLookupResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Lookup AI fallito');
    } finally {
      setAiLookupBusy(false);
    }
  };

  const applyAiLookupToForm = () => {
    if (!aiLookupResult) return;
    setForm({
      codice: aiLookupForm.codice || form.codice,
      descrizione: aiLookupResult.descrizione || '',
      categoria: aiLookupResult.categoria || 'altro',
      sottocategoria: aiLookupResult.sottocategoria || '',
      produttore: aiLookupResult.produttore || aiLookupForm.produttore || '',
      prezzo_acquisto: String(aiLookupResult.prezzo_acquisto || ''),
      prezzo_vendita: String(aiLookupResult.prezzo_vendita || ''),
      unita_misura: aiLookupResult.unita_misura || 'pz',
      ean: aiLookupResult.ean || '',
      note: (aiLookupResult.specifiche_tecniche || []).join(' • ') || aiLookupResult.note || '',
    });
    setAiLookupDialog(false);
    setEditing(null);
    setOpenCreate(true);
    toast.success('Dati AI applicati — rivedi e salva');
  };

  const handleAiDescribe = async () => {
    if (!form.codice && !form.produttore) {
      toast.error('Inserisci almeno il codice o produttore'); return;
    }
    setAiDescribeBusy(true);
    try {
      const r = await axios.post(`${API}/materiali-catalogo/ai-describe`, {
        codice: form.codice,
        produttore: form.produttore,
        categoria: form.categoria,
        keywords: form.descrizione,
      }, { withCredentials: true });
      if (r.data?.descrizione) {
        setForm({ ...form, descrizione: r.data.descrizione });
        toast.success('Descrizione AI generata');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generazione descrizione fallita');
    } finally {
      setAiDescribeBusy(false);
    }
  };

  const totalCount = useMemo(
    () => categorie.reduce((sum, c) => sum + (c.count || 0), 0),
    [categorie]
  );

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="materiali-catalogo-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Package className="h-6 w-6" /> Catalogo materiali</h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalCatalog.toLocaleString('it-IT')} prodotti totali{items.length < totalCatalog ? ` — mostrando i primi ${items.length}, affina con la ricerca` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setAiImportDialog(true)} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="ai-import-btn"><BrainCircuit className="h-4 w-4 mr-2" />Importa con AI</Button>
          <Button variant="outline" onClick={() => { setAiLookupForm({ codice:'', produttore:'', url:'' }); setAiLookupResult(null); setAiLookupDialog(true); }} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="ai-lookup-btn"><Globe className="h-4 w-4 mr-2" />Lookup AI</Button>
          <Button variant="outline" onClick={handleSeed} data-testid="seed-catalog"><Sparkles className="h-4 w-4 mr-2" />Catalogo standard</Button>
          <label className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-sm" data-testid="import-excel-label">
            <Upload className="h-4 w-4" />
            <span>{importing ? 'Import…' : 'Import Excel'}</span>
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportExcel} disabled={importing} />
          </label>
          <label className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-purple-300 bg-purple-50 hover:bg-purple-100 cursor-pointer text-sm text-purple-800" data-testid="import-metel-label" title="Formato standard fornitori elettrici italiani (.txt METEL - delimitato o fixed-width)">
            <Upload className="h-4 w-4" />
            <span>{importingMetel ? 'Import METEL…' : 'Import METEL (.txt)'}</span>
            <input type="file" accept=".txt" className="hidden" onChange={handleImportMetel} disabled={importingMetel} />
          </label>
          <Button onClick={() => { setEditing(null); setForm(emptyForm); setOpenCreate(true); }} className="bg-slate-900 text-white" data-testid="create-materiale"><Plus className="h-4 w-4 mr-2" />Nuovo materiale</Button>
          <Button variant="outline" onClick={handleDeleteAll} disabled={items.length === 0} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-all-catalog"><Trash2 className="h-4 w-4 mr-2" />Elimina tutti</Button>
        </div>
      </div>

      {/* Filtri + AI search */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs text-slate-500 flex items-center gap-2">
              Cerca {aiSearchActive && <Badge className="bg-purple-100 text-purple-700 text-[10px]"><BrainCircuit className="h-3 w-3 mr-1" />Risultati AI ({aiResults.length})</Badge>}
            </Label>
            <div className="relative flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); if (aiSearchActive) clearAiSearch(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAiSearch(); } }}
                  placeholder="es. 'telecamera bullet 4MP esterno Hikvision' (Invio per ricerca AI)"
                  className="pl-10 pr-9"
                  data-testid="search-materiali"
                />
                {searchPending && search && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" data-testid="search-pending-spinner" />
                )}
              </div>
              <Button
                type="button"
                variant={aiSearchActive ? 'default' : 'outline'}
                onClick={aiSearchActive ? clearAiSearch : handleAiSearch}
                disabled={aiSearchBusy}
                className={aiSearchActive ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}
                data-testid="ai-search-btn"
              >
                {aiSearchBusy ? (
                  <><Wand2 className="h-4 w-4 mr-2 animate-pulse" />Ricerca…</>
                ) : aiSearchActive ? (
                  <><Wand2 className="h-4 w-4 mr-2" />Esci AI</>
                ) : (
                  <><Wand2 className="h-4 w-4 mr-2" />Ricerca AI</>
                )}
              </Button>
            </div>
          </div>
          <div className="w-48">
            <Label className="text-xs text-slate-500">Categoria</Label>
            <Select value={filterCategoria} onValueChange={(v) => { setFilterCategoria(v); if (aiSearchActive) clearAiSearch(); }}>
              <SelectTrigger data-testid="filter-categoria"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte ({totalCount})</SelectItem>
                {(categorie || []).filter(c => c && c.id && String(c.id).trim()).map(c => <SelectItem key={c.id} value={c.id}>{c.label || c.id} ({c.count || 0})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Label className="text-xs text-slate-500">Marca / Produttore</Label>
            <Select value={filterProduttore} onValueChange={(v) => { setFilterProduttore(v); if (aiSearchActive) clearAiSearch(); }}>
              <SelectTrigger data-testid="filter-produttore"><SelectValue placeholder="Tutte le marche" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le marche</SelectItem>
                {(produttoriList || []).filter(p => p && p.nome && String(p.nome).trim()).map(p => <SelectItem key={p.nome} value={p.nome}>{p.nome} ({p.count || 0})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Barra bulk action */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-sky-50 border border-sky-200 rounded-md px-4 py-2 flex-wrap gap-2" data-testid="bulk-bar">
          <div className="text-sm text-sky-900"><b>{selectedIds.size}</b> articoli selezionati</div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}>Annulla</Button>
            <Button size="sm" onClick={openAddToMagFromSelection} disabled={bulkBusy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="bulk-add-to-mag-btn">
              <Package className="h-3.5 w-3.5 mr-1" /> Aggiungi al Magazzino
            </Button>
            <Button size="sm" onClick={bulkDelete} disabled={bulkBusy} className="bg-red-600 text-white hover:bg-red-700" data-testid="bulk-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> {bulkBusy ? 'Elimino…' : `Elimina ${selectedIds.size}`}
            </Button>
          </div>
        </div>
      )}

      {/* Tabella */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10 pl-3">
                  <input type="checkbox" className="h-4 w-4 accent-slate-900 cursor-pointer" checked={items.length > 0 && selectedIds.size === items.length} onChange={(e) => toggleSelectAll(e.target.checked)} data-testid="select-all" />
                </TableHead>
                <TableHead>Codice</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Produttore</TableHead>
                {aiSearchActive && <TableHead className="text-center">AI</TableHead>}
                <TableHead className="text-right">Listino</TableHead>
                <TableHead className="text-right">Acquisto</TableHead>
                <TableHead className="text-center">Sconto</TableHead>
                <TableHead className="text-right">Vendita</TableHead>
                <TableHead>UM</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const rows = aiSearchActive ? aiResults : items;
                const colCount = aiSearchActive ? 12 : 11;  // +1 checkbox, +2 listino/sconto
                if (rows.length === 0) {
                  return (
                    <TableRow><TableCell colSpan={colCount} className="text-center py-10 text-slate-500">
                      <Package className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                      {aiSearchActive ? 'Nessun risultato AI — prova a riformulare la query' : 'Nessun materiale — usa "Carica catalogo standard" per partire'}
                    </TableCell></TableRow>
                  );
                }
                return rows.map(it => (
                  <TableRow key={it.id} className={`border-slate-100 ${selectedIds.has(it.id) ? 'bg-blue-50/50' : ''}`} data-testid={`materiale-${it.id}`}>
                    <TableCell className="pl-3">
                      <input type="checkbox" className="h-4 w-4 accent-slate-900 cursor-pointer" checked={selectedIds.has(it.id)} onChange={() => toggleSelect(it.id)} data-testid={`select-${it.id}`} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{it.codice}</TableCell>
                    <TableCell className="text-sm">
                      {it.descrizione}
                      {aiSearchActive && it.ai_motivazione && (
                        <div className="text-[11px] text-purple-700 mt-1 italic">{it.ai_motivazione}</div>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{(it.categoria || '').replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{it.produttore || '-'}</TableCell>
                    {aiSearchActive && (
                      <TableCell className="text-center">
                        <Badge className={`text-[10px] ${it.ai_score >= 80 ? 'bg-green-100 text-green-700' : it.ai_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                          {it.ai_score}%
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell className="text-right text-sm text-slate-500">{it.prezzo_listino ? `€ ${Number(it.prezzo_listino).toLocaleString('it-IT', {minimumFractionDigits: 2})}` : '-'}</TableCell>
                    <TableCell className="text-right text-sm">{it.prezzo_acquisto ? `€ ${Number(it.prezzo_acquisto).toLocaleString('it-IT', {minimumFractionDigits: 2})}` : '-'}</TableCell>
                    <TableCell className="text-center text-xs">{it.sconto_pct ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{Number(it.sconto_pct).toFixed(1)}%</Badge> : <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell className="text-right text-sm">{it.prezzo_vendita ? `€ ${Number(it.prezzo_vendita).toLocaleString('it-IT', {minimumFractionDigits: 2})}` : '-'}</TableCell>
                    <TableCell className="text-sm">{it.unita_misura || 'pz'}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" onClick={() => openAddSingleToMag(it)} title="Aggiungi al Magazzino" data-testid={`add-to-mag-${it.id}`}><Package className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(it)} title="Modifica" data-testid={`edit-materiale-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDelete(it.id)} title="Elimina"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog create/edit */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica' : 'Nuovo'} materiale</DialogTitle>
            <DialogDescription>Compila i campi del prodotto. Usa "✨ AI" per generare automaticamente la descrizione tecnica.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Descrizione</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={handleAiDescribe} disabled={aiDescribeBusy} className="h-7 text-xs text-purple-700 hover:bg-purple-50" data-testid="ai-describe-btn">
                    <Wand2 className={`h-3.5 w-3.5 mr-1 ${aiDescribeBusy ? 'animate-pulse' : ''}`} />
                    {aiDescribeBusy ? 'Genero…' : 'Descrivi con AI'}
                  </Button>
                </div>
                <Input value={form.descrizione} onChange={setField('descrizione')} required data-testid="form-descrizione" />
              </div>
              <div><Label>Codice</Label><Input value={form.codice} onChange={setField('codice')} required className="font-mono" data-testid="form-codice" /></div>
              <div><Label>EAN/Barcode</Label><Input value={form.ean} onChange={setField('ean')} className="font-mono" /></div>
              <div><Label>Categoria</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({...form, categoria: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(categorie || []).filter(c => c && c.id && String(c.id).trim()).map(c => <SelectItem key={c.id} value={c.id}>{c.label || c.id}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Sottocategoria</Label><Input value={form.sottocategoria} onChange={setField('sottocategoria')} /></div>
              <div><Label>Produttore</Label><Input value={form.produttore} onChange={setField('produttore')} /></div>
              <div><Label>Unità di misura</Label><Input value={form.unita_misura} onChange={setField('unita_misura')} placeholder="pz / m / kg" /></div>
              <div><Label>Prezzo listino € <span className="text-[10px] text-slate-400">(pre-sconto)</span></Label><Input type="number" step="0.01" value={form.prezzo_listino} onChange={setField('prezzo_listino')} data-testid="form-prezzo-listino" /></div>
              <div><Label>Prezzo acquisto € <span className="text-[10px] text-slate-400">(netto pagato)</span></Label><Input type="number" step="0.01" value={form.prezzo_acquisto} onChange={setField('prezzo_acquisto')} data-testid="form-prezzo-acquisto" /></div>
              <div><Label>Sconto % <span className="text-[10px] text-emerald-600">(auto se vuoto)</span></Label><Input type="number" step="0.01" value={form.sconto_pct} onChange={setField('sconto_pct')} placeholder={form.prezzo_listino && form.prezzo_acquisto ? `auto: ${(((parseFloat(form.prezzo_listino) - parseFloat(form.prezzo_acquisto)) / parseFloat(form.prezzo_listino) * 100) || 0).toFixed(2)}` : ''} data-testid="form-sconto-pct" /></div>
              <div><Label>Prezzo vendita € <span className="text-[10px] text-slate-400">(al cliente)</span></Label><Input type="number" step="0.01" value={form.prezzo_vendita} onChange={setField('prezzo_vendita')} data-testid="form-prezzo-vendita" /></div>
              <div className="col-span-2"><Label>Note</Label><Input value={form.note} onChange={setField('note')} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenCreate(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white" data-testid="form-submit">{editing ? 'Salva' : 'Crea'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Import dialog */}
      <Dialog open={aiImportDialog} onOpenChange={setAiImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BrainCircuit className="h-5 w-5 text-purple-600" />Importa listino con AI</DialogTitle>
            <DialogDescription>Descrivi un listino o categoria di prodotti e l'AI genererà automaticamente fino a 60 articoli pronti per il catalogo.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAiImport} className="space-y-4">
            <div>
              <Label>Descrizione del listino</Label>
              <Textarea
                value={aiImportForm.prompt}
                onChange={(e) => setAiImportForm({...aiImportForm, prompt: e.target.value})}
                rows={5}
                placeholder="es. Listino completo allarmi Ajax 2026 con sirene, sensori, centrali e accessori, prezzi medi a listino"
                required
                data-testid="ai-import-prompt"
              />
              <p className="text-xs text-slate-500 mt-1">L'AI genererà codici tecnici reali del produttore, descrizioni, prezzi e categoria. Powered by GPT-5.2.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria suggerita (opz.)</Label>
                <Select value={aiImportForm.categoria || 'auto'} onValueChange={(v) => setAiImportForm({...aiImportForm, categoria: v === 'auto' ? '' : v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-rileva</SelectItem>
                    {(categorie || []).filter(c => c && c.id && String(c.id).trim()).map(c => <SelectItem key={c.id} value={c.id}>{c.label || c.id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Numero max prodotti</Label>
                <Input type="number" min={1} max={60} value={aiImportForm.max_items} onChange={(e) => setAiImportForm({...aiImportForm, max_items: e.target.value})} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAiImportDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={aiImportBusy} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="ai-import-submit">
                <BrainCircuit className={`h-4 w-4 mr-2 ${aiImportBusy ? 'animate-pulse' : ''}`} />
                {aiImportBusy ? 'Generazione…' : 'Importa con AI'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Lookup dialog */}
      <Dialog open={aiLookupDialog} onOpenChange={setAiLookupDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-purple-600" />Lookup prodotto con AI</DialogTitle>
            <DialogDescription>Inserisci un codice articolo o URL del fornitore: l'AI compilerà automaticamente descrizione, categoria, produttore, EAN e prezzo indicativo.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAiLookup} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Codice articolo</Label><Input value={aiLookupForm.codice} onChange={(e) => setAiLookupForm({...aiLookupForm, codice: e.target.value})} placeholder="es. DS-2CD2143G2-IU" className="font-mono" data-testid="ai-lookup-codice" /></div>
              <div><Label>Produttore (opz.)</Label><Input value={aiLookupForm.produttore} onChange={(e) => setAiLookupForm({...aiLookupForm, produttore: e.target.value})} placeholder="es. Hikvision" /></div>
              <div><Label>URL fornitore (opz.)</Label><Input value={aiLookupForm.url} onChange={(e) => setAiLookupForm({...aiLookupForm, url: e.target.value})} placeholder="https://..." /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAiLookupDialog(false)}>Chiudi</Button>
              <Button type="submit" disabled={aiLookupBusy} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="ai-lookup-submit">
                <Globe className={`h-4 w-4 mr-2 ${aiLookupBusy ? 'animate-pulse' : ''}`} />
                {aiLookupBusy ? 'Cerco…' : 'Cerca con AI'}
              </Button>
            </div>

            {aiLookupResult && (
              <div className="border border-purple-200 bg-purple-50/50 rounded-md p-4 space-y-2 mt-3" data-testid="ai-lookup-result">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-purple-900">Risultato AI</h4>
                  <Badge className={`text-[10px] ${aiLookupResult.confidence >= 70 ? 'bg-green-100 text-green-700' : aiLookupResult.confidence >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>
                    Confidence {aiLookupResult.confidence || 0}%
                  </Badge>
                </div>
                <div className="text-sm"><b>Descrizione:</b> {aiLookupResult.descrizione}</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <div><b>Categoria:</b> {aiLookupResult.categoria}</div>
                  <div><b>Sottocategoria:</b> {aiLookupResult.sottocategoria || '-'}</div>
                  <div><b>Produttore:</b> {aiLookupResult.produttore || '-'}</div>
                  <div><b>EAN:</b> {aiLookupResult.ean || '-'}</div>
                  <div><b>Prezzo acquisto:</b> € {aiLookupResult.prezzo_acquisto || 0}</div>
                  <div><b>Prezzo vendita:</b> € {aiLookupResult.prezzo_vendita || 0}</div>
                </div>
                {aiLookupResult.specifiche_tecniche?.length > 0 && (
                  <div className="text-xs text-slate-600">
                    <b>Specifiche:</b>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {aiLookupResult.specifiche_tecniche.map((s, i) => <li key={`spec-${i}`}>{s}</li>)}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end pt-1">
                  <Button type="button" onClick={applyAiLookupToForm} className="bg-slate-900 text-white" data-testid="ai-lookup-apply"><Plus className="h-4 w-4 mr-2" />Crea con questi dati</Button>
                </div>
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* iter100m: Dialog "Aggiungi al Magazzino" */}
      <Dialog open={addToMagDialog} onOpenChange={setAddToMagDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto" data-testid="add-to-mag-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-600" />
              Aggiungi al Magazzino ({addToMagRows.length} articoli)
            </DialogTitle>
            <DialogDescription>
              Imposta la quantità iniziale e giacenza minima per ogni articolo.
              Se un articolo è già presente nel Magazzino (stesso codice), verranno
              aggiornati solo nome/prezzo/EAN — la giacenza rimane invariata.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {addToMagRows.map((row, idx) => (
              <div key={row.catalogo_id} className="border border-slate-200 rounded-md p-3 bg-slate-50">
                <div className="text-xs text-slate-500 font-mono mb-1">{row.codice} {row.produttore && `• ${row.produttore}`}</div>
                <div className="text-sm font-medium text-slate-800 mb-2 truncate">{row.descrizione}</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Q.tà iniziale ({row.unita_misura})</Label>
                    <Input type="number" step="0.01" min="0" value={row.qty_initial} onChange={(e) => {
                      const v = e.target.value;
                      setAddToMagRows(prev => prev.map((r, i) => i === idx ? { ...r, qty_initial: v } : r));
                    }} data-testid={`add-mag-qty-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Giacenza minima</Label>
                    <Input type="number" step="0.01" min="0" value={row.qty_min} onChange={(e) => {
                      const v = e.target.value;
                      setAddToMagRows(prev => prev.map((r, i) => i === idx ? { ...r, qty_min: v } : r));
                    }} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Posizione (opz.)</Label>
                    <Input value={row.location} onChange={(e) => {
                      const v = e.target.value;
                      setAddToMagRows(prev => prev.map((r, i) => i === idx ? { ...r, location: v } : r));
                    }} placeholder="es. Scaffale A-3" />
                  </div>
                </div>
                {row.prezzo_listino > 0 && <div className="text-xs text-slate-500 mt-1">Prezzo listino: € {Number(row.prezzo_listino).toFixed(2)}</div>}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setAddToMagDialog(false)} disabled={addToMagBusy}>Annulla</Button>
            <Button onClick={submitAddToMag} disabled={addToMagBusy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="add-to-mag-submit">
              <Package className="h-4 w-4 mr-2" /> {addToMagBusy ? 'Aggiunta…' : `Aggiungi ${addToMagRows.length} al Magazzino`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
